import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { InputControlBase } from '../forms/input-control.base';

/**
 * TimePickerComponent is a custom time picker component that extends InputControlBase.
 * It allows users to select hours and minutes in either 24-hour or limit less format.
 */
@Component({
    selector: 'app-time-picker',
    templateUrl: './time-picker.component.html',
    styleUrls: ['./time-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TimePickerComponent extends InputControlBase {
  @Input() is24HourFormat: boolean = true;

  hours: number;
  minutes: number;
  private updating = false;

  constructor() {
    super();
  }

  // When the control is set, subscribe to its value changes
  onControlSet() {
    super.onControlSet();
    this.setTime(this.control.value);
    this.subscription.add(
      this.control.valueChanges.subscribe(val => {
        if (!this.updating) {
          this.setTime(val);
        }
      })
    );
  }

  // Update hours when hour input changes
  onHourChange(hour: string): void {
    this.updating = true;
    const previousValue = this.control.value;
    this.hours = Number(hour);
    this.updateTime(previousValue);
    this.updating = false;
  }

  // Update minutes when minute input changes
  onMinuteChange(minute: string): void {
    this.updating = true;
    const previousValue = this.control.value;
    this.minutes = Number(minute);
    this.updateTime(previousValue);
    this.updating = false;
  }

  // Set time from a string value
  private setTime(value: string): void {
    this.updating = true;
    if (value) {
      const parts = value.split(':');
      this.hours = +parts[0];
      this.minutes = +parts[1];
      this.updateTime(value);
    }
    this.updating = false;
  }

  // Update time and emit value change
  private updateTime(previousValue: string): void {
    this.control.markAsDirty();
    this.handle24HoursFormat();
    this.updateControlValue(previousValue);
  }

  // Update control value and emit value change
  private updateControlValue(previousValue: string): void {
    const time = this.getTimeString();
    this.control.setValue(time);
    this.valueChange.emit({ formSnapshot: this.control.parent.value, newValue: this.control.value, previousValue: previousValue });
  }

  // Pad single digit numbers with a leading zero
  private pad(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }

  // Get time string in HH:MM format
  private getTimeString(): string {
    if (this.hours && !this.minutes) this.minutes = 0;
    if (!this.hours && this.minutes) this.hours = 0;

    return `${this.pad(this.hours)}:${this.pad(this.minutes)}`;
  }

  // Handle 24-hour format by limiting hours to 0-24 and minutes to 0-60
  private handle24HoursFormat(): void {
    if (!this.is24HourFormat) return;

    this.hours = this.hours > 24 ? 24 : this.hours < 0 ? 0 : this.hours;
    this.minutes = this.minutes > 60 ? 60 : this.minutes < 0 ? 0 : this.minutes;
  }
}
